import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Box,
  IconButton,
  Tooltip,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Cloud,
  Plus,
  Trash2,
  Eye,
  Code,
  Edit2,
  Globe,
  Database,
  Clock,
  CircuitBoard
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from './api';
import ConfirmModal from './components/ConfirmModal';
import AddEditServiceDialog from './components/services/AddEditServiceDialog';

const ServiceManagement = () => {
  const [services, setServices] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteService, setDeleteService] = useState(null);

  const loadServices = async () => {
    try {
      const response = await api.getAllServices();
      setServices(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Failed to load services');
      setServices([]);
    }
  };

  useEffect(() => {
    loadServices();
  }, []);

  const handleEditService = async (service) => {
    try {
      const serviceDetails = await api.getServiceById(service.id);
      setSelectedService(serviceDetails);
      setDialogOpen(true);
    } catch (error) {
      console.error('Error loading service details:', error);
      toast.error('Failed to load service details');
    }
  };

  const handleTestService = async (service) => {
    try {
      await api.testService(service.id);
      toast.success('Service test completed successfully');
    } catch (error) {
      console.error('Service test error:', error);
      toast.error('Service test failed');
    }
  };

  const handleDeleteService = (service) => {
    setDeleteService(service);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.deleteService(deleteService.id);
      toast.success('Service deleted successfully');
      setConfirmOpen(false);
      loadServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const handleAddNew = () => {
    setSelectedService(null);
    setDialogOpen(true);
  };

  const handleSaveService = async (serviceData) => {
    try {
      if (selectedService) {
        await api.updateService(selectedService.id, serviceData);
        toast.success('Service updated successfully');
      } else {
        await api.createService(serviceData);
        toast.success('Service created successfully');
      }
      setDialogOpen(false);
      loadServices();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Failed to save service');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedService(null);
  };

  const getServiceIcon = (type) => {
    switch (type) {
      case 'rest':
        return <Cloud size={20} />;
      case 'soap':
        return <CircuitBoard size={20} />;
      default:
        return <Globe size={20} />;
    }
  };

  // Dashboard metrics calculation
  const metrics = {
    totalServices: services.length,
    restServices: services.filter(s => s.type === 'rest').length,
    soapServices: services.filter(s => s.type === 'soap').length,
    totalMethods: services.reduce((acc, service) => acc + (service.methods?.length || 0), 0)
  };

  return (
    <Box sx={{
      p: 3,
      backgroundColor: '#f8f4ff',
      minHeight: '100vh',
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header Section */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flex: '0 0 auto'
      }}>
        <Typography variant="h4" sx={{ color: '#edbc45', fontWeight: 600 }}>
          Web Servisleri
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={handleAddNew}
          sx={{
            backgroundColor: '#edbc45',
            color: '#000',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#d4a63a' }
          }}
        >
          Yeni Servis Ekle
        </Button>
      </Box>

      {/* Metrics Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flex: '0 0 auto' }}>
        <Card sx={{ backgroundColor: '#edbc45', color: '#000', minWidth: 240, maxWidth: 320 }}>
          <CardContent sx={{ p: 1.8, '&:last-child': { pb: 1.8 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Database size={22} />
              <Typography variant="subtitle1" fontWeight={600}>Toplam Servis</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700 }}>{metrics.totalServices}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 240, maxWidth: 320 }}>
          <CardContent sx={{ p: 1.8, '&:last-child': { pb: 1.8 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Cloud size={22} />
              <Typography variant="subtitle1" fontWeight={600}>REST Servisleri</Typography>
            </Box>
            <Typography variant="h3" sx={{ color: '#edbc45', fontWeight: 700 }}>{metrics.restServices}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 240, maxWidth: 320 }}>
          <CardContent sx={{ p: 1.8, '&:last-child': { pb: 1.8 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <CircuitBoard size={22} />
              <Typography variant="subtitle1" fontWeight={600}>SOAP Servisleri</Typography>
            </Box>
            <Typography variant="h3" sx={{ color: '#edbc45', fontWeight: 700 }}>{metrics.soapServices}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 240, maxWidth: 320 }}>
          <CardContent sx={{ p: 1.8, '&:last-child': { pb: 1.8 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Clock size={22} />
              <Typography variant="subtitle1" fontWeight={600}>Toplam Metod</Typography>
            </Box>
            <Typography variant="h3" sx={{ color: '#edbc45', fontWeight: 700 }}>{metrics.totalMethods}</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Services Table */}
      <TableContainer
        component={Paper}
        sx={{
          flex: '1 1 auto',
          overflow: 'auto',
          maxHeight: 'calc(100vh - 280px)'
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8f4ff' }}>
              <TableCell sx={{ fontWeight: 600 }}>Servis Adı</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tip</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>URL</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Metodlar</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Yetkilendirme</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services.map((service) => (
              <TableRow key={service.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getServiceIcon(service.type)}
                    <Typography>{service.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={service.type.toUpperCase()}
                    size="small"
                    sx={{
                      backgroundColor: service.type === 'rest' ? '#e8f5e9' : '#f8f4ff',
                      color: service.type === 'rest' ? '#2e7d32' : '#edbc45',
                      fontWeight: 600
                    }}
                  />
                </TableCell>
                <TableCell>{service.url}</TableCell>
                <TableCell>{service.methods?.length || 0} metod</TableCell>
                <TableCell>
                  <Chip
                    label={service.authenticationType || 'Yok'}
                    size="small"
                    sx={{ backgroundColor: '#f8f4ff' }}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Düzenle">
                    <IconButton
                      size="small"
                      onClick={() => handleEditService(service)}
                      sx={{ color: '#edbc45' }}
                    >
                      <Edit2 size={16} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Test">
                    <IconButton
                      size="small"
                      onClick={() => handleTestService(service)}
                      sx={{ color: '#2e7d32' }}
                    >
                      <Eye size={16} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sil">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteService(service)}
                      sx={{ color: '#ef5350' }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <AddEditServiceDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveService}
        initialData={selectedService}
      />

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Servisi Sil"
        content="Bu servisi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
        type="yesNo"
      />
    </Box>
  );
};

export default ServiceManagement;